import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { Assignment, TrackChanges, SwapHoriz, Assessment } from '@mui/icons-material';
import { motion } from 'framer-motion';
import type { LandingCopy, LandingPalette } from './landingCopy';

const icons = [
  <Assignment key="1" sx={{ fontSize: 28, color: '#7DD3FC' }} />,
  <TrackChanges key="2" sx={{ fontSize: 28, color: '#60A5FA' }} />,
  <SwapHoriz key="3" sx={{ fontSize: 28, color: '#4ADE80' }} />,
  <Assessment key="4" sx={{ fontSize: 28, color: '#FBBF24' }} />,
];

type WorkflowSectionProps = {
  copy: LandingCopy['workflow'];
  palette: LandingPalette;
};

const WorkflowSection: React.FC<WorkflowSectionProps> = ({ copy, palette }) => (
  <Box component="section" sx={{ py: { xs: 4, md: 5 }, bgcolor: palette.pageBg, color: palette.text }}>
    <Container maxWidth="lg">
      <Typography
        sx={{
          fontFamily: '"Outfit", "Cairo", sans-serif',
          fontWeight: 700,
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          mb: 1,
        }}
      >
        {copy.title}
      </Typography>
      <Typography sx={{ color: palette.muted, mb: 2.5, maxWidth: 560 }}>
        {copy.subtitle}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 2.5,
        }}
      >
        {copy.steps.map((step, index) => (
          <motion.div
            key={step.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ delay: index * 0.07, duration: 0.4 }}
          >
            <Box
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${palette.border}`,
                background: palette.cardBg,
              }}
            >
              <Typography sx={{ color: palette.accent, fontWeight: 800, mb: 1.5, fontSize: '0.85rem' }}>
                {step.n}
              </Typography>
              <Box sx={{ mb: 1.5 }}>{icons[index]}</Box>
              <Typography sx={{ fontFamily: '"Outfit", "Cairo", sans-serif', fontWeight: 700, mb: 1 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.muted, lineHeight: 1.6 }}>
                {step.description}
              </Typography>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Container>
  </Box>
);

export default WorkflowSection;
